import { useEffect } from 'react';
import type { PublicUser } from '../types/gateway';
import styles from './MentionPopup.module.css';

interface MentionPopupProps {
  /** Filtered roster matching the current `@query`. */
  candidates: PublicUser[];
  highlightIndex: number;
  /** Anchor coordinates relative to the viewport (for `position: fixed`). */
  anchor: { left: number; top: number } | null;
  onSelect: (user: PublicUser) => void;
  onHover: (index: number) => void;
}

export function MentionPopup({ candidates, highlightIndex, anchor, onSelect, onHover }: MentionPopupProps) {
  useEffect(() => {
    // No-op: arrow-key handling lives in the InputBar (where the textarea is).
  }, []);

  if (!anchor || candidates.length === 0) return null;

  return (
    <div
      className={styles.popup}
      role="listbox"
      style={{ left: anchor.left, top: anchor.top }}
    >
      {candidates.map((u, i) => (
        <button
          key={u.username}
          type="button"
          role="option"
          aria-selected={i === highlightIndex}
          className={`${styles.row} ${i === highlightIndex ? styles.rowActive : ''}`}
          onMouseEnter={() => onHover(i)}
          onMouseDown={(e) => {
            e.preventDefault(); // keep textarea focused
            onSelect(u);
          }}
        >
          <span className={styles.avatar} aria-hidden>{u.name.charAt(0).toUpperCase()}</span>
          <span className={styles.meta}>
            <span className={styles.name}>{u.name}</span>
            <span className={styles.sub}>@{u.username}{u.email ? ` · ${u.email}` : ''}</span>
          </span>
          <span className={styles.role}>{u.uiRole}</span>
        </button>
      ))}
    </div>
  );
}
